/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Shapes of the in-store (cold storage) API: GET /fresh/in-store/state and friends.
 * The api client turns ISO strings in responses into Dates, so timestamps are typed as
 * either and always read through `new Date()`.
 */

export type ApiTimestamp = string | Date

/** Where a marker sits on its location's floor plan: fractions 0..1 of the surface. */
export interface Position {
  x: number
  y: number
}

export interface Threshold {
  lowCelsius: number
  highCelsius: number
}

export interface InStoreFacility {
  facilityId: number
  name: string
  latitude: number | null
  longitude: number | null
  address: {
    street: string | null
    city: string | null
    state: string | null
    postcode: string | null
    country: string | null
  }
}

export interface InStoreGateway {
  umdId: number
  deviceName: string
  online: boolean
  sampleTime: ApiTimestamp | null
  offlineSampleTime: ApiTimestamp | null
  position: Position | null
}

export type SensorType = 'temperature' | 'door'
export type TagState = 'ok' | 'open-circuit' | 'short-circuit'

/**
 * GET /fresh/in-store/blast/state only: how long a temperature sensor has been in its
 * location's range, or when its recent trend is estimated to get it there.
 */
export interface BlastTiming {
  inRangeSince: ApiTimestamp | null
  /** In range with no out-of-range reading in the API's lookback window. */
  inRangeSinceBeyondWindow: boolean
  estimatedInRangeAt: ApiTimestamp | null
}

/** An active alert the user is subscribed to (same rule as the asset map). */
export interface InStoreAlert {
  id: number
  name: string
  severity: string
  raisedTime: ApiTimestamp
}

export interface InStoreSensor {
  /** Fresh MonitoringPointId. */
  id: number
  name: string
  type: SensorType
  tagId: number | null
  serialNumber: number | null
  /** Null when no tag is mapped to the point. */
  online: boolean | null
  sampleTime: ApiTimestamp | null
  /** When the tag went offline; null while online or with no tag mapped. */
  offlineSince: ApiTimestamp | null
  temperatureCelsius: number | null
  tagState: TagState | null
  humidity: { enabled: boolean; percent: number | null; sampleTime: ApiTimestamp | null } | null
  /** Per-point range, kept by the API for later; the screen uses the location's. */
  threshold: Threshold | null
  door: { state: 'open' | 'closed' | null } | null
  position: Position | null
  /** Blast freezer state only; null for doors. */
  timing?: BlastTiming | null
  /** The location's active alerts that name this sensor. */
  activeAlerts: InStoreAlert[]
}

/** An in-store asset: a cold room, freezer or loading dock. The screen calls it a location. */
export interface InStoreAsset {
  id: number
  name: string
  comments: string | null
  /** The acceptable range for every temperature sensor of the location. */
  threshold: Threshold | null
  location: InStoreFacility | null
  gateway: InStoreGateway | null
  sensors: InStoreSensor[]
  /** Every active alert on the location, newest first. */
  activeAlerts: InStoreAlert[]
}

/** One opening of a door: from GET /fresh/insights/asset/door-activities. */
export interface DoorEvent {
  id: number
  openedDate: ApiTimestamp
  /** Null while the door is still open. */
  closedDate: ApiTimestamp | null
}

export interface ImageLink {
  entityType: string
  entityId: number
  usage: string
}

export interface ImageMeta {
  id: number
  companyId: number
  name: string
  contentType: string
  byteSize: number
  updatedAt: ApiTimestamp
  links: ImageLink[]
}

export interface HistoryPoint {
  time: ApiTimestamp
  value: number
}

export type MarkerType = 'sensor' | 'gateway'

export interface MarkerRef {
  type: MarkerType
  /** Fresh MonitoringPointId for a sensor, the AssetId for a gateway. */
  id: number
}

export interface Marker extends MarkerRef, Position {}

/** The image role a location's floor plan is stored under. */
export const FLOOR_PLAN_USAGE = 'cold-storage-map'

export const markerKey = ({ type, id }: MarkerRef): string => `${type}:${id}`
